"use server";

// ============================================================
// CLUBHUB – Spenden: alle Schreibwege (Server Actions)
// ============================================================
// Ohne Sitzung (Demo-Modus, Musterbestand) wird nichts gespeichert,
// die Masken melden nur Erfolg. Mit Sitzung: Mandant und Recht aus
// der Sitzung (`requirePermission("donations.write")`), dann dieselbe
// Pruefung wie im Dialog (`lib/spenden/pruefen.ts`), dann der Kern
// (`lib/spenden/speicher.ts`) — mit der `clubId` der Sitzung, nie
// einer aus dem Formular.
//
// Zuwendungsbestätigungen sperrt der Kern selbst, wenn der Verein
// nicht ausstellen darf (nicht gemeinnuetzig, Bescheid zu alt,
// Pflichtangaben fehlen) — die Action reicht nur den Satz weiter.
// ============================================================

import { revalidatePath } from "next/cache";
import type { CommunicationChannel } from "@/generated/prisma/enums";
import type { ActionResult } from "@/lib/action-result";
import { aktuell } from "@/lib/current";
import { Abgelehnt, nummerText, planeBelege } from "@/lib/spenden/ausstellen";
import { heuteIso, jahrVon } from "@/lib/spenden/format";
import { pruefeAufruf, pruefeSpende } from "@/lib/spenden/pruefen";
import type { Ausstellauftrag } from "@/lib/spenden/views";

function neuLaden() {
  revalidatePath("/spenden", "layout");
}

async function istMuster(): Promise<boolean> {
  return (await aktuell()).quelle === "muster";
}

type Kern = typeof import("@/lib/spenden/speicher");
type Db = (typeof import("@/lib/db"))["db"];

/** Gemeinsamer Rahmen: Recht pruefen, Kern laden, fachliche Absagen in Saetze uebersetzen. */
async function mitRecht<T extends object>(tun: (k: { kern: Kern; db: Db; wer: { clubId: string; userId: string } }) => Promise<T>): Promise<ActionResult<T>> {
  const [{ requirePermission, Forbidden }, { db }, kern] = await Promise.all([import("@/lib/tenant"), import("@/lib/db"), import("@/lib/spenden/speicher")]);
  try {
    const ctx = await requirePermission("donations.write");
    const ergebnis = await tun({ kern, db, wer: { clubId: ctx.clubId, userId: ctx.userId } });
    neuLaden();
    return { success: true, ...ergebnis };
  } catch (e) {
    if (e instanceof Forbidden) return { error: "Dafür fehlt dir das Recht „Spenden bearbeiten“." };
    if (e instanceof Abgelehnt) return { error: e.message, field: e.field };
    throw e;
  }
}

// ── Spenden ──────────────────────────────────────────────────

export async function spendeSpeichern(id: string | null, formData: FormData): Promise<ActionResult<{ id: string; text: string }>> {
  const p = pruefeSpende(formData, heuteIso());
  if (!p.ok) return { error: p.error, field: p.field };
  if (await istMuster()) {
    const { MOCK_CONTACTS } = await import("@/lib/mock/core");
    const name = p.wert.isAnonymous ? null : (MOCK_CONTACTS.find((c) => c.id === p.wert.contactId)?.displayName ?? null);
    return { success: true, id: id ?? "sp-001", text: name ?? "anonym" };
  }
  return mitRecht(async ({ kern, db, wer }) => {
    const r = id ? await kern.aendereSpende(db, wer, id, p.wert) : await kern.legeSpendeAn(db, wer, p.wert);
    let text = "anonym";
    if (!p.wert.isAnonymous && p.wert.contactId) {
      const c = await db.contact.findFirst({ where: { id: p.wert.contactId, clubId: wer.clubId }, select: { kind: true, firstName: true, lastName: true, organizationName: true } });
      if (c) text = c.kind !== "PERSON" && c.organizationName ? c.organizationName : [c.lastName, c.firstName].filter(Boolean).join(", ") || "Spende";
    }
    return { id: r.id, text };
  });
}

export async function spendeLoeschen(id: string): Promise<ActionResult> {
  if (await istMuster()) return { success: true };
  return mitRecht(async ({ kern, db, wer }) => {
    await kern.loescheSpende(db, wer, id);
    return {};
  });
}

// ── Aufrufe ──────────────────────────────────────────────────

export async function aufrufSpeichern(id: string | null, formData: FormData): Promise<ActionResult<{ id: string }>> {
  const p = pruefeAufruf(formData);
  if (!p.ok) return { error: p.error, field: p.field };
  if (await istMuster()) return { success: true, id: id ?? "k-01" };
  return mitRecht(async ({ kern, db, wer }) => (id ? kern.aendereAufruf(db, wer, id, p.wert) : kern.legeAufrufAn(db, wer, p.wert)));
}

export async function aufrufAktivSetzen(id: string, aktiv: boolean): Promise<ActionResult> {
  if (await istMuster()) return { success: true };
  return mitRecht(async ({ kern, db, wer }) => {
    await kern.setzeAufrufAktiv(db, wer, id, Boolean(aktiv));
    return {};
  });
}

export async function aufrufLoeschen(id: string): Promise<ActionResult<{ titel: string }>> {
  if (await istMuster()) {
    const { mockSpendenstand } = await import("@/lib/mock/spenden");
    return { success: true, titel: mockSpendenstand().aufrufe.find((a) => a.id === id)?.title ?? "Aufruf" };
  }
  return mitRecht(async ({ kern, db, wer }) => kern.loescheAufruf(db, wer, id));
}

// ── Bestätigungen ────────────────────────────────────────────

export async function bestaetigungenAusstellen(auftraege: Ausstellauftrag[]): Promise<ActionResult<{ ids: string[]; nummern: string[]; hinweis?: string }>> {
  if (!Array.isArray(auftraege) || auftraege.length === 0) return { error: "Es ist niemand ausgewählt." };
  const heute = heuteIso();

  if (await istMuster()) {
    // Nichts schreiben — nur die Nummern zeigen, die vergeben wuerden.
    const [{ ladeSpendenstand }] = await Promise.all([import("@/lib/spenden/daten")]);
    const s = await ladeSpendenstand();
    if (!s.pflicht.bereit) return { error: s.pflicht.sperrgrund ?? "Der Verein darf gerade keine Bestätigungen ausstellen." };
    try {
      const namen = new Map([...s.kontakte.values()].map((c) => [c.id, c.displayName]));
      const plan = planeBelege(auftraege, new Map(s.saetze.spenden.map((d) => [d.id, d])), namen);
      const beispiel = s.bestaetigungen.find((b) => !b.cancelledAt)?.id ?? "";
      return {
        success: true,
        ids: plan.map(() => beispiel),
        nummern: plan.map((_, i) => nummerText(jahrVon(heute), s.naechsteNummer + i)),
        hinweis: "Musterbestand: nichts gespeichert.",
      };
    } catch (e) {
      if (e instanceof Abgelehnt) return { error: e.message };
      throw e;
    }
  }

  return mitRecht(async ({ kern, db, wer }) => {
    const { ablageFuer } = await import("@/lib/ablage/ablage");
    const ablage = await ablageFuer(wer.clubId);
    const r = await kern.stelleAus(db, ablage, wer, auftraege, heute);
    return {
      ids: r.belege.map((b) => b.id),
      nummern: r.belege.map((b) => b.number),
      ...(r.pdfFehler > 0 ? { hinweis: `${r.pdfFehler} PDF ließen sich noch nicht ablegen — sie entstehen beim ersten Öffnen.` } : {}),
    };
  });
}

export async function bestaetigungenVersenden(ids: string[], via: CommunicationChannel | "AUTO"): Promise<ActionResult<{ anzahl: number; hinweis?: string }>> {
  if (!Array.isArray(ids) || ids.length === 0) return { error: "Es ist keine Bestätigung ausgewählt." };
  if (via !== "EMAIL" && via !== "POST" && via !== "AUTO") return { error: "Unbekannter Versandweg." };
  if (await istMuster()) return { success: true, anzahl: ids.length };
  // Mail mit PDF-Anhang (Vorlage DONATION_RECEIPT) kommt mit dem Bereich Nachrichten;
  // bis dahin vermerkt clubhub nur den Versand per Post.
  return mitRecht(async ({ kern, db, wer }) => {
    const r = await kern.markiereVersendet(db, wer, ids, via, false);
    return {
      anzahl: r.anzahl,
      ...(r.ohneMail > 0 ? { hinweis: `${r.ohneMail} für den Mailversand bleiben offen — Mail ist noch nicht angebunden. Lade die PDFs und schick sie selbst.` } : {}),
    };
  });
}

export async function bestaetigungWiderrufen(id: string, grund: string): Promise<ActionResult<{ nummer: string }>> {
  const text = String(grund ?? "").trim();
  if (text.length < 5) return { error: "Bitte kurz begründen, warum die Bestätigung widerrufen wird.", field: "grund" };
  if (await istMuster()) {
    const { mockSpendenstand } = await import("@/lib/mock/spenden");
    return { success: true, nummer: mockSpendenstand().bestaetigungen.find((b) => b.id === id)?.number ?? "" };
  }
  return mitRecht(async ({ kern, db, wer }) => ({ nummer: (await kern.widerrufe(db, wer, id, text)).number }));
}
